import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen, CheckCircle, Clock, Star, Users, Zap, FileText,
  MessageCircle, ArrowRight, Award, Shield, TrendingUp
} from 'lucide-react';
import useSEO from '../utils/useSEO';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const AssignmentWritingPage = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'Assignment Writing Service for Students | Fast Delivery | AGO Innovations',
    description:
      'Get expert assignment writing help for engineering, medical, B.Sc, and 11th–12th students in India. Affordable subscription plans starting ₹399. Fast delivery guaranteed.',
    keywords:
      'assignment writing service, college assignment help, engineering assignment help, assignment writing India, student assignment service, affordable assignment help, assignment subscription plan',
    canonical: 'https://agoinnovations.in/assignment-writing-service',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Assignment Writing Service',
      provider: { '@type': 'Organization', name: 'AGO Innovations', url: 'https://agoinnovations.in' },
      description:
        'Professional assignment writing service for students across engineering, medical, B.Sc, and school streams in India.',
      areaServed: 'IN',
      serviceType: 'Academic Writing',
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: '399',
        highPrice: '1999',
        priceCurrency: 'INR',
        offerCount: '4',
      },
    },
  });

  const features = [
    { icon: <BookOpen className="w-6 h-6" />, title: 'All Subjects Covered', desc: 'Engineering, Medical, B.Sc, Commerce, Arts, and school projects.' },
    { icon: <Clock className="w-6 h-6" />, title: 'Fast Turnaround', desc: 'Assignments delivered on time, every time. Deadline-focused.' },
    { icon: <Shield className="w-6 h-6" />, title: 'Plagiarism-Free', desc: 'Every assignment is original and crafted for your requirements.' },
    { icon: <Star className="w-6 h-6" />, title: 'Expert Writers', desc: 'Experienced academic writers who understand Indian curriculum.' },
    { icon: <TrendingUp className="w-6 h-6" />, title: 'Subscription Plans', desc: 'Save more with monthly, 3-month, 6-month or annual plans.' },
    { icon: <MessageCircle className="w-6 h-6" />, title: '24/7 Support', desc: 'Get answers via WhatsApp or email anytime you need help.' },
  ];

  const plans = [
    { category: '🔧 Engineering', months3: '₹699', months6: '₹1,199', year: '₹1,999' },
    { category: '🩺 Medical Students', months3: '₹499', months6: '₹849', year: '₹1,349' },
    { category: '🎓 B.Sc Students', months3: '₹799', months6: '₹1,199', year: '₹1,999' },
    { category: '📘 11th – 12th', months3: '₹399', months6: '₹999', year: '₹1,899' },
  ];

  const faqs = [
    {
      q: 'What subjects do you cover for assignment writing?',
      a: 'We cover all major streams — Engineering (all branches), Medical, B.Sc, Commerce, Arts, and school subjects from 9th to 12th grade.',
    },
    {
      q: 'How fast will my assignment be delivered?',
      a: 'Standard delivery is within 24–72 hours. Urgent assignments can be delivered faster on request.',
    },
    {
      q: 'Are the assignments original and plagiarism-free?',
      a: 'Yes. Every assignment is written from scratch by our experts to match your specific requirements and university guidelines.',
    },
    {
      q: 'What is the subscription plan benefit?',
      a: 'Subscription plans give you unlimited assignment submissions for your plan duration at a fixed price — much cheaper than paying per assignment.',
    },
    {
      q: 'How do I submit my assignment requirement?',
      a: 'Simply visit our Learn & Earn portal, subscribe to a plan, and submit your requirement with the topic, deadline, and guidelines.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* HERO */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 pt-28 pb-20 px-4 text-white text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400" />
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-indigo-500/20 rounded-full blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative z-10 max-w-4xl mx-auto"
        >
          <span className="inline-block bg-yellow-400/20 border border-yellow-400/40 text-yellow-300 text-sm font-semibold px-5 py-1.5 rounded-full mb-5">
            ✍️ Student Services — Assignment Writing
          </span>
          <h1 className="text-4xl md:text-6xl font-black mb-5 leading-tight">
            Assignment Writing Service
            <span className="block text-yellow-400">for Students in India</span>
          </h1>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            Get your assignments written by experts — engineering, medical, B.Sc, and school.
            Affordable plans, fast delivery, and zero plagiarism.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/learn/customer-work')}
              className="bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"
            >
              Submit Requirement <ArrowRight className="w-5 h-5" />
            </motion.button>
            <a
              href="https://wa.me/91XXXXXXXXXX?text=Hi%20AGO%20Innovations!%20I%20need%20assignment%20writing%20help."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-400 text-white font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" /> WhatsApp Us
            </a>
          </div>
          {/* Trust signals */}
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-blue-200">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Plagiarism-Free</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> On-Time Delivery</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> All Streams Covered</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Plans from ₹399</span>
          </div>
        </motion.div>
      </section>

      {/* FEATURES */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
              Why Students Choose AGO Innovations
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              From engineering to school level — we've got every assignment need covered.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 mb-4">
                  {f.icon}
                </div>
                <h3 className="text-gray-900 font-bold text-base mb-2">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
              Affordable Assignment Subscription Plans
            </h2>
            <p className="text-gray-500 text-lg">Choose your plan — the longer the plan, the more you save.</p>
          </motion.div>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="overflow-x-auto rounded-2xl shadow-xl border border-blue-100"
          >
            <table className="w-full text-center border-collapse">
              <thead>
                <tr className="bg-gradient-to-r from-blue-800 to-indigo-700 text-white">
                  <th className="px-6 py-5 text-left font-bold text-sm uppercase tracking-wider">Category</th>
                  <th className="px-6 py-5 font-bold text-sm uppercase tracking-wider">3 Months</th>
                  <th className="px-6 py-5 font-bold text-sm uppercase tracking-wider">6 Months</th>
                  <th className="px-6 py-5 font-bold text-sm uppercase tracking-wider">1 Year</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((row, i) => (
                  <tr key={i} className={`border-b border-blue-50 hover:bg-blue-50 transition-colors ${i % 2 !== 0 ? 'bg-indigo-50/40' : 'bg-white'}`}>
                    <td className="px-6 py-5 text-left font-bold text-gray-800 text-sm">{row.category}</td>
                    <td className="px-6 py-5">
                      <span className="inline-block bg-blue-100 text-blue-800 font-bold px-4 py-1.5 rounded-full text-sm">{row.months3}</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="inline-block bg-indigo-100 text-indigo-800 font-bold px-4 py-1.5 rounded-full text-sm">{row.months6}</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold px-4 py-1.5 rounded-full text-sm shadow">{row.year}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
          <div className="text-center mt-8">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/learn/customer-work/subscription')}
              className="bg-blue-700 hover:bg-blue-600 text-white font-bold px-10 py-4 rounded-full shadow-lg transition-colors"
            >
              Get Started — Subscribe Now
            </motion.button>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">How It Works</h2>
            <p className="text-gray-500 text-lg">3 simple steps to get your assignment done.</p>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-8">
            {[
              { step: '01', icon: <Users className="w-8 h-8" />, title: 'Subscribe to a Plan', desc: 'Choose your subscription plan based on your stream and duration.' },
              { step: '02', icon: <FileText className="w-8 h-8" />, title: 'Submit Your Requirement', desc: 'Share your assignment topic, deadline, and any specific instructions.' },
              { step: '03', icon: <Award className="w-8 h-8" />, title: 'Receive Your Assignment', desc: 'Get a well-researched, formatted assignment delivered to you on time.' },
            ].map((s, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-100 relative"
              >
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-700 text-white text-xs font-black px-4 py-1.5 rounded-full">
                  Step {s.step}
                </div>
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 mx-auto mb-5 mt-3">
                  {s.icon}
                </div>
                <h3 className="font-black text-gray-900 text-base mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-500">Everything you need to know about our assignment writing service.</p>
          </motion.div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-gray-50 rounded-2xl p-6 border border-gray-100"
              >
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{faq.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-800 to-indigo-900 text-white text-center">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-2xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl font-black mb-4">
            Need Assignment Help?
          </h2>
          <p className="text-blue-200 text-lg mb-8">
            Get your assignment completed before the deadline. Subscribe now and submit your first requirement today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              onClick={() => navigate('/learn/customer-work')}
              className="bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-bold px-8 py-4 rounded-full shadow-lg transition-colors"
            >
              Submit Requirement
            </motion.button>
            <a
              href="https://wa.me/91XXXXXXXXXX?text=Hi!%20I%20need%20assignment%20writing%20service."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-400 text-white font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" /> WhatsApp Us
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default AssignmentWritingPage;
